import {
    animate,
    animation,
    group,
    query,
    sequence,
    style,
    useAnimation,
} from "@angular/animations";

/** Html class to be query in animation */
const CURRENTGUIZMO: string = ".currentGuizmo";
const CURRENTSTRAP: string = ".currentStrap";
const NEXTSTRAP: string = ".nextStrap";
const NEXTGUIZMO: string = ".nextGuizmo";

/** Step duration in animation */
export const duration: string = "500ms";

/** velocity curves for animations */
const CUBIC_BEZIER_GUIZMO_FISRT: string = `${duration} cubic-bezier(.46,0,0,.99)`;
const CUBIC_BEZIER_GUIZMO_SECOND: string = `${duration} cubic-bezier(.87,.01,.45,.99)`;
const CUBIC_BEZIER_STRAP: string = `${duration} linear`;

/**
 * Reusable fade animation
 * @param time : animation delay
 * @param opacity : opacity to apply
 */
export const fade = animation([
    animate("{{ time }}", style({ opacity: "{{ opacity }}" })),
]);

/**
 * Reusable slide animation
 * @param time : animation delay
 * @param direction : translateX value to apply
 */
export const slide = animation([
    animate("{{ time }}", style({ transform: "translateX({{direction}})" })),
]);

/**
 * Reusable resize animation
 * @param time : animation delay
 * @param direction : translateX value to apply to keep
 * @param scale : scale value to apply
 */
export const resize = animation([
    animate(
        "{{ time }}",
        style({ transform: "translateX({{direction}}) scale({{ scale }})" }),
    ),
]);

/**
 * Reusable guizmo animation
 * @param nextGuizmoDirection : translateX value to apply on nextGuizmo
 * @param currentGuizmoDirection : translateX value to apply on currentGuizmo
 */
export const guizmoAnim = animation([
    query(NEXTSTRAP, [
        useAnimation(fade, {
            params: { time: "0ms", opacity: "0" },
        }),
    ]),
    query(NEXTGUIZMO, [
        useAnimation(fade, {
            params: { time: "0ms", opacity: "0" },
        }),
        useAnimation(resize, {
            params: {
                time: "0ms",
                direction: "{{nextGuizmoDirection}}",
                scale: "0.5",
            },
        }),
    ]),
    sequence([
        query(CURRENTSTRAP, [
            useAnimation(fade, {
                params: {
                    time: duration,
                    opacity: "0",
                },
            }),
        ]),
        group([
            query(CURRENTGUIZMO, [
                sequence([
                    useAnimation(slide, {
                        params: {
                            time: CUBIC_BEZIER_GUIZMO_FISRT,
                            direction: "{{currentGuizmoDirection}}",
                        },
                    }),
                    group([
                        useAnimation(fade, {
                            params: {
                                time: CUBIC_BEZIER_GUIZMO_SECOND,
                                opacity: "0",
                            },
                        }),
                        useAnimation(resize, {
                            params: {
                                time: CUBIC_BEZIER_GUIZMO_SECOND,
                                direction: "{{currentGuizmoDirection}}",
                                scale: "0.5",
                            },
                        }),
                    ]),
                ]),
            ]),
            query(NEXTGUIZMO, [
                sequence([
                    group([
                        useAnimation(resize, {
                            params: {
                                time: CUBIC_BEZIER_GUIZMO_FISRT,
                                direction: "{{nextGuizmoDirection}}",
                                scale: "1",
                            },
                        }),
                        useAnimation(fade, {
                            params: {
                                time: CUBIC_BEZIER_GUIZMO_FISRT,
                                opacity: "1",
                            },
                        }),
                    ]),
                    useAnimation(slide, {
                        params: {
                            time: CUBIC_BEZIER_GUIZMO_SECOND,
                            direction: "0",
                        },
                    }),
                ]),
            ]),
        ]),
        query(NEXTSTRAP, [
            useAnimation(fade, {
                params: {
                    time: duration,
                    opacity: "1",
                },
            }),
        ]),
    ]),
]);

/**
 * Reusable strap animation
 * @param nextStrapDirection : translateX value to apply on nextStrap
 */
export const strapAnim = animation([
    query(NEXTSTRAP, [
        group([
            useAnimation(fade, {
                params: { time: "0ms", opacity: "0" },
            }),
            useAnimation(slide, {
                params: {
                    time: "0ms",
                    direction: "{{nextStrapDirection}}",
                },
            }),
        ]),
    ]),
    sequence([
        query(CURRENTSTRAP, [
            useAnimation(fade, {
                params: { time: duration, opacity: "0" },
            }),
        ]),
        query(NEXTSTRAP, [
            group([
                useAnimation(fade, {
                    params: { time: duration, opacity: "1" },
                }),
                useAnimation(slide, {
                    params: {
                        time: CUBIC_BEZIER_STRAP,
                        direction: "0",
                    },
                }),
            ]),
        ]),
    ]),
]);

/**
 * Triggered by component when currentIndex < nextIndex
 * Make appear new item on right side
 */
export const nextGuizmoAnim = animation([
    useAnimation(guizmoAnim, {
        params: {
            nextGuizmoDirection: "35%",
            currentGuizmoDirection: "-45%",
        },
    }),
]);

export const nextStrapAnim = animation([
    useAnimation(strapAnim, {
        params: {
            nextStrapDirection: "40%",
        },
    }),
]);

/**
 * Triggered by component when currentIndex > nextIndex
 * Make appear new item on left side
 */
export const previousGuizmoAnim = animation([
    useAnimation(guizmoAnim, {
        params: {
            nextGuizmoDirection: "-45%",
            currentGuizmoDirection: "35%",
        },
    }),
]);

export const previousStrapAnim = animation([
    useAnimation(strapAnim, {
        params: {
            nextStrapDirection: "-40%",
        },
    }),
]);
